import {
  Fragment,
  useCallback,
  useState,
  useMemo,
  useRef,
  useEffect
} from "react";
import {
  Button,
  NavDropdown,
  OverlayTrigger,
  Popover,
  Tooltip
} from "react-bootstrap";
import useItem, { ItemContext } from "../../hooks/useItem";
import {
  PendingEditsContext,
  createPendingEditsStore,
  useHasPendingEdits
} from "../../hooks/usePendingEdits";
import { useDataSetStatus, useDataStateSelector } from "../../hooks/useData";
import {
  draft,
  draftKey,
  mergeDraft,
  presentConflicts,
  scopeLabel,
  useDraftCount,
  useDraftSave,
  useDraftSaving,
  useFormConflict,
  useReloadItems,
  useResolveConflicts
} from "../../hooks/draft";
import { useMainContext } from "../../hooks/useMainContext";
import useConfirmNavigation from "../../hooks/useConfirmNavigation";
import { touchesRow } from "../../hooks/draft/resolve";
import { coupledScope } from "../../hooks/draft/exit";
import { formRow, stashForm } from "../../hooks/draft/stashForm";
import { draftMineOf } from "../../hooks/draft/present";
import { describe, merge } from "../../hooks/draft/diff";
import { storedRow } from "../draft/useScopeEdit";
import ConflictBanner from "../draft/conflictBanner";
import {
  dependentsSentence,
  wholeDraftsOf
} from "../draft/conflictDialog";
import { db } from "../../db";
import { useNavigate, useLocation } from "react-router";
import useClipboard, { useClipboardCopy } from "../../hooks/useClipboard";
import useSession from "../../hooks/useSession";
import useUserFields from "../../hooks/useUserFields";
import { Formik, Form } from "formik";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLock,
  faSpinner,
  faAngleDoubleLeft,
  faClipboard,
  faCalendar,
  faCopy,
  faPaste,
  faExclamationCircle,
  faGripVertical
} from "@fortawesome/free-solid-svg-icons";
import niceTry from "nice-try";
import capitalize from "lodash.capitalize";
import Container from "../container";
import NameAndDescription from "../nameAndDescription";
import RefBox from "./ref";
import SaveBar from "../draft/saveBar";
import cloneDeep from "lodash.clonedeep";
import diff from "variable-diff";
import Editor from "@monaco-editor/react";
import styles from "./styles.module.scss";
import "./style.scss";

import FormScheduleDiagram from "../scheduleDiagram/form";

const emptyObj = {};
// The id of the route of a new item (/blocks/newItem, also for a copy)
const NEW_ITEM = "newItem";
const propertyTitle = (id) => db.peek("properties", id)?.title;

// A save made by this user (lastSaved of a row): by the nickname (the oid,
// unique) when the row has it, else by the display name (the token's name:
// not unique, the only author of the rows saved before the server wrote the
// nickname: a namesake's row passes too, see claimSaved). user: { nickname,
// name }.
export const savedByUser = (lastSaved, { nickname, name } = {}) =>
  lastSaved?.nickname != null
    ? Boolean(nickname) && lastSaved.nickname === nickname
    : Boolean(name) && lastSaved?.username === name;

// The stored row of a create that went through (its answer lost) does not
// have what the Save would send now (sent: the form's values through
// onSubmit), i.e. something was typed after that Save. Compared as the form
// sees the rows (describe: booleans as 1/0, the sub-block ids); id,
// version, lastSaved, refs and refsComputed are the server's, and a field
// only the stored row has is none of the user's.
const changedSince = (accessor, stored, sent) => {
  const { lines, more } = describe({
    base: stored,
    theirs: stored,
    mine: { ...stored, ...sent, id: stored.id },
    accessor,
    limit: 1
  });
  return lines.length > 0 || more > 0;
};

// A Save told "Not saved" had gone through (its response lost; draft: OUR
// OWN SAVES)
const EARLIER_SAVED = "Saved: your earlier Save had gone through";

// Reads the clipboard here, so a clipboard change re-renders only this menu
// and not the whole editor form.
function ClipboardMenu({ label, accessor, tenant, onCopy, onPaste }) {
  const { clipboard } = useClipboard();
  const clip = useMemo(
    () => niceTry(() => JSON.parse(clipboard)) ?? emptyObj,
    [clipboard]
  );

  return (
    <NavDropdown
      title={<FontAwesomeIcon icon={faClipboard} />}
      id="nav-dropdown-conditions-clip"
      className={styles.extraMenu}
    >
      <NavDropdown.Item eventKey="1.1" onClick={onCopy}>
        <FontAwesomeIcon icon={faCopy} className={styles.copyIcon} />
        Copy {label}
      </NavDropdown.Item>
      <NavDropdown.Item
        eventKey="1.2"
        onClick={() => onPaste(clip.item, clip)}
        disabled={clip._ares !== accessor || clip.tenant !== tenant}
        className={clip._ares !== accessor ? styles.disabledMenuItem : ""}
      >
        <FontAwesomeIcon icon={faPaste} className={styles.pasteIcon} />
        Paste {label}
      </NavDropdown.Item>
    </NavDropdown>
  );
}

export default function Item(props) {
  const [initiated, setInitiated] = useState(false);
  const emptyValuesObj = useMemo(() => ({}), []);
  const formRef = useRef();
  // Edits not yet handed over by the conditions boxes of the form: only the
  // Save button subscribes to it, so a change re-renders nothing else.
  const [pendingEdits] = useState(() => createPendingEditsStore());
  const nameRef = useRef();
  const activeBodyRef = useRef(false);
  const [isSaving, setIsSavingState] = useState(false);
  // Read when the row of the form changes: a new version while our own Save
  // runs, or the first one after it saved the row, is that Save's
  const savingRef = useRef(false);
  const ownSaveRef = useRef(false);
  const setIsSaving = useCallback((saving) => {
    savingRef.current = saving;
    setIsSavingState(saving);
  }, []);
  const setDataStatus = useDataSetStatus();
  // The author of a save (savedByUser): the server writes the token's name
  // (the display name, not unique) in lastSaved.username, and the user's
  // nickname (the oid) in lastSaved.nickname (rows saved before it did
  // have none)
  const {
    tenant,
    branch,
    name: userName,
    nickname: userNickname
  } = useUserFields("tenant", "branch", "name", "nickname");
  const { copy } = useClipboardCopy();
  const canSave = useDataStateSelector((state) => state.canSave);
  // The form has changes: its dirty (props.dirty, or Formik's) or an edit of
  // a conditions box not handed over yet. Read only when a server row of
  // the form's row arrives (the hold, draft.incoming) or changes (useItem:
  // a copy is not reinitialised while modified), never per keystroke.
  const formDirtyRef = useRef(() => false);
  const item = useItem(props, { formDirtyRef });
  const navigate = useNavigate();
  const location = useLocation();
  const showTitleAndDesc =
    props.showTitleAndDesc === undefined ? true : props.showTitleAndDesc;
  const [locked, setLocked] = useState(false);
  const { isActive } = useSession();
  const [currentValues, setCurrentValues] = useState(
    item.item ?? emptyValuesObj
  );
  const [isCopied, setIsCopied] = useState(false);
  // saveExtra(data) => { items, deletes, modifiche }: saved in the same
  // request as the item (one commit), e.g. the endpoint of a variant page.
  // draftScope: the draft of the page (e.g. "properties:<id>"), saved by the
  // Save of the form in the same request; alone when the form is unchanged.
  const {
    onInit,
    onValidate,
    onSubmit,
    onSubmitted,
    saveExtra,
    accessor,
    draftScope,
    // coupleDraft(values, initialValues): the form and the draft of its scope
    // are split halves of an operation right now (see coupledScope)
    coupleDraft,
    // stashExtra(values) => ops: the rows of the page changed with the form
    // (draft.change ops, e.g. the endpoint of a variant page), put into the
    // draft with the form row when the form is stashed (see stash below)
    stashExtra,
    // onStashed(): the stash committed (the page clears what it put in the
    // draft); onDiscarded(form): the changes of the form were thrown away
    // (Reset, or the Discard of the draft that resets the form), or only
    // what the page built on rows reloaded or discarded; after a reload of
    // conflicts that leaves the form's values (formAfterReload) form is the
    // Formik bag, and the page takes back from the values what it put there
    // for those changes
    onStashed,
    onDiscarded,
    // stashWithErrors(errors, values): the errors that do not stop the stash
    // of a row created in the draft (e.g. a new variant with no layout yet);
    // its Save stays refused while they are there
    stashWithErrors,
    // onRebased(form): the form was set on their version with the user's
    // changes ("Apply my changes on their version": form, its Formik bag);
    // the page marks again what it keeps outside the form (e.g. the
    // lastSaved.date mark of the pending conditions of the variant page)
    onRebased,
    // extraConflicts(): the rows the page saves with the form (saveExtra)
    // that someone else saved since the page changed them, as the conflicts
    // of a 409 ({ type, id, baseVersion, storedVersion, lastSaved, action }):
    // the Save is refused before it leaves and the conflict dialog opens
    // (e.g. the endpoint of the variant page, pinned on its base)
    extraConflicts,
    // banner: shown with the form's conflict banner (e.g. the endpoint of
    // the variant page saved by someone else); a node, or banner(getForm)
    // (getForm(): the Formik bag of the form now)
    banner,
    // leaveWhenDeleted (default true): the form's row gone after a reload
    // of conflicts (a colleague deleted it), the Item leaves for its list;
    // false: the page shows something else (the variant page: its tabs)
    leaveWhenDeleted = true,
    dirty: isDirty
  } = props;
  const saveDraft = useDraftSave(draftScope);
  const resolveConflicts = useResolveConflicts();
  const reloadItems = useReloadItems();
  const scopeSaving = useDraftSaving(draftScope);
  const { requestConfirm, createNotification } = useMainContext();

  formDirtyRef.current = () => {
    if (pendingEdits.isPending()) return true;
    const formik = formRef.current;
    if (!formik) return false;
    return Boolean(
      isDirty
        ? isDirty(formik.values, item.item ?? emptyValuesObj)
        : formik.dirty
    );
  };

  // The row of the form can't be drafted (writing it would reinitialise the
  // form): the draft store refuses it while the form is open. With a
  // draftScope the form can be stashed: before a navigation that stays in
  // the scope (a variant tab, the property and its variant pages),
  // useConfirmNavigation asks it to put its changes into the draft
  // (draft.stashOpenForms) instead of asking to leave without saving.
  // isDirty: while the form has changes, a colleague's save of its row is
  // held (THE HOLD, README): MemoryDB stays on the row the form was loaded
  // from, the form is not reinitialised, its Save is refused (the banner
  // says so); a clean form takes their row as before.
  // releaseRef: the EXACT release of the registration (the store frees the
  // row for a change only with it: the stash's closing, openFormRelease).
  const releaseRef = useRef(null);
  const registerRef = useRef(null);
  const stashRef = useRef(null);
  const ownRowUnderRef = useRef(null);
  useEffect(() => {
    if (item.isNew || !item.prevItemId) return;
    const options = {
      ...(draftScope && {
        scope: draftScope,
        stash: (destination) => stashRef.current?.(destination)
      }),
      isDirty: () => formDirtyRef.current(),
      onOwnSave: (row, base, how) => ownRowUnderRef.current?.(row, how)
    };
    const register = () => {
      releaseRef.current = draft.registerOpenForm(
        accessor,
        item.prevItemId,
        options
      );
    };
    registerRef.current = register;
    register();

    return () => {
      // The current registration (a stash registers the form again)
      releaseRef.current?.();
      releaseRef.current = null;
      registerRef.current = null;
    };
  }, [accessor, draftScope, item.isNew, item.prevItemId]);

  // The stash (see stashForm.js): "none" | "invalid" | "stashed", or throws
  // the error of a refused change (the navigation tells the user why). Read
  // when it runs: the values, the errors and the props of now.
  stashRef.current = (destination) => {
    const formik = formRef.current;
    if (!formik || !draftScope) return "none";
    const original = item.item ?? emptyValuesObj;
    // The errors of the values of now: Formik's are those of its last
    // validation, which may not have run yet (e.g. a condition of the
    // variant page, kept outside the form, just changed). An asynchronous
    // validation can't be waited for: its last result, then.
    const fresh = validateValues(formik.values);
    if (fresh?.then) fresh.catch(() => {});
    const errors = fresh?.then ? formik.errors : fresh;
    const created = draft.storedOriginal(accessor, item.prevItemId) === null;
    const invalid =
      Object.keys(errors ?? emptyObj).length > 0 &&
      !(created && stashWithErrors?.(errors, formik.values));

    const outcome = stashForm({
      store: draft,
      scope: draftScope,
      accessor,
      id: item.prevItemId,
      release: releaseRef.current,
      values: formik.values,
      item: item.item,
      stored: storedRow(accessor, item.prevItemId),
      submit: onSubmit,
      derive: item.fromRow,
      dirty: isDirty ? isDirty(formik.values, original) : formik.dirty,
      errors,
      invalid,
      pending: pendingEdits.isPending(),
      renamed: item.itemId !== item.prevItemId,
      onMain: branch === "main",
      extra: stashExtra
    });

    if (outcome === "stashed") {
      // The change released the form (closing): still mounted (e.g. the
      // navigation is refused later), it holds its row again, and is clean
      // on its drafted row (what it shows is in the draft now; also when
      // the row did not change: a condition of the variant page, which
      // only marks the form modified)
      registerRef.current?.();
      onStashed?.();
      // As the item derived from it (fromRow): the re-read of the drafted
      // row then compares equal, no second reset
      const drafted = db.get(accessor, item.prevItemId);
      if (drafted) formik.resetForm({ values: item.fromRow(drafted) });
    }
    return outcome;
  };
  // The row the stash of the form would draft now (formRow: its values as
  // its Save sends them, comparable with the rows of the draft), undefined
  // with no form. Read when asked (the rebase of its drafted row).
  const formRowRef = useRef(null);
  formRowRef.current = () => {
    const values = formRef.current?.values;
    if (!values) return undefined;
    return formRow({
      values,
      item: item.item,
      stored: storedRow(accessor, item.prevItemId),
      submit: onSubmit,
      derive: item.fromRow
    });
  };
  // For a page that closes the open form itself (e.g. deleting the open
  // variant: change(ops, { dryRun: true, closing: openFormRelease() })): the
  // release of the registration now, or null
  const openFormRelease = useCallback(() => releaseRef.current, []);

  // The form is clean again (no change, no edit of a conditions box
  // pending): what the hold kept for its row is applied (their row, their
  // delete), so a clean form shows the stored row; a Reset with a conflict
  // held loads their version. Nothing held: nothing happens.
  const formSettled = useCallback(() => {
    if (item.isNew || !item.prevItemId) return;
    draft.formSettled(accessor, item.prevItemId);
  }, [accessor, item.isNew, item.prevItemId]);

  // The row of the form saved by someone else (a new version, not from our
  // Save): Formik reinitialises the form on it, so the half of an operation
  // kept in the form is lost (e.g. the nodes of the property tree of pages
  // created or deleted in the draft), while the rows of the draft that
  // depend on the row of the form would stay (orphans). Those drafts are
  // discarded, after the reinitialise, and the user is told.
  // A safety net since the hold: a dirty form keeps its row (their save is
  // held, Reload and Rebase discard the dependents themselves, resolve.js),
  // so this runs for a clean form only: their row let through, or the hold
  // applied when the form turned clean (formSettled). Then the tree the
  // pages were added to is the stored one again.
  // Not our own rebase ("Apply my changes on their version"): their version
  // reaches the form because the user put their changes on it (rebaseRef,
  // set before the row is written, read here before the effect below takes
  // it), so the operations of the draft stay with the form's tree. Nor the
  // row of a save of ours brought by another way than its response (a full
  // reload, the socket: its response not come yet, or lost): the drafts of
  // the operations went with it (draft.isOwnSave).
  const rowVersion = item.item?.version;
  const rowVersionRef = useRef(rowVersion);
  // The rebase of the form (see rebaseConflicts below): { version, values }
  // until the form is set on the row of that version
  const rebaseRef = useRef(null);
  useEffect(() => {
    const previous = rowVersionRef.current;
    rowVersionRef.current = rowVersion;
    if (previous === undefined || rowVersion === undefined) return;
    if (previous === rowVersion) return;
    const ours =
      savingRef.current ||
      ownSaveRef.current ||
      (rebaseRef.current !== null &&
        rebaseRef.current.version === Number(rowVersion ?? 0));
    ownSaveRef.current = false;
    if (ours || !draftScope || item.isNew || !item.prevItemId) return;
    if (
      draft.isOwnSave?.(
        accessor,
        item.prevItemId,
        db.peek(accessor, item.prevItemId),
        { version: previous }
      )
    )
      return;

    const dependents =
      draft.dependentsOf?.([draftKey(accessor, item.prevItemId)]) ?? [];
    const scopes = [
      ...new Set(
        dependents
          .map((dependent) =>
            typeof dependent === "string" ? dependent : dependent?.scope
          )
          .filter(Boolean)
      )
    ];
    if (scopes.length === 0) return;

    scopes.forEach((scope) => draft.discard(scope));
    onDiscarded?.();
    const who = item.item?.lastSaved?.username ?? "Someone";
    const what = item.item?.title ?? item.prevItemId;
    const drafts = scopes
      .map((scope) => scopeLabel(scope, propertyTitle))
      .join(", ");
    createNotification(
      "warning",
      `${who} saved ${what} meanwhile, and it is shown as they saved it: the pages you had added, copied or deleted in it were discarded, with all the unsaved changes of ${drafts}.`
    );
    // Only a new version of the row
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowVersion]);

  // The draft scope the form is coupled with now (an operation split between
  // them, e.g. a drag in the property tree), or null
  const coupledNow = useCallback(
    () =>
      coupledScope({
        dirty: formRef.current?.dirty,
        draftScope,
        couple: coupleDraft,
        values: formRef.current?.values,
        initialValues: formRef.current?.initialValues
      }),
    [coupleDraft, draftScope]
  );

  // Reset discards the coupled draft too (asking first when it has changes).
  // A form opened on a drafted row (stashed) goes back to that row.
  const resetFormAndDraft = useCallback(() => {
    const scope = coupledNow();
    const count = scope ? draft.count(scope) : 0;
    const reset = () => {
      formRef.current?.handleReset();
      onDiscarded?.();
      if (scope) draft.discard(scope);
    };

    if (count === 0) reset();
    else
      requestConfirm(
        `Reset the form and discard the ${count} unsaved change${
          count === 1 ? "" : "s"
        } of the page?`,
        reset
      );
  }, [coupledNow, onDiscarded, requestConfirm]);

  // Discard of the draft resets the form: the reset, or null. When the form
  // is coupled with the draft, or when its own row is drafted in the scope
  // (stashed, or created by the draft: a new variant), the discard takes the
  // row back (or removes it), and the form goes with it.
  const formToDiscard = useCallback(() => {
    const ownRow =
      draftScope &&
      !item.isNew &&
      item.prevItemId &&
      draft.ownerOf(accessor, item.prevItemId) === draftScope;

    return coupledNow() || ownRow
      ? () => {
          formRef.current?.resetForm();
          onDiscarded?.();
        }
      : null;
  }, [accessor, coupledNow, draftScope, item.isNew, item.prevItemId, onDiscarded]);

  // A reload of conflicts ("Discard my changes and reload" of the dialog of
  // a refused Save, "Reload them" of the SaveBar): asked BEFORE the reload
  // (the page's changes of now; the form's row may leave the draft), it
  // gives what the form does once the rows are reloaded,
  // (discarded: [scope], the drafts discarded whole) => void. The form's
  // changes go (reset, and onDiscarded) when they were built on a row
  // reloaded: its own row; or the whole draft of its scope discarded (a
  // row reloaded is one a created row depends on) while the form goes with
  // that draft (formToDiscard: coupled, e.g. the tree of the property form
  // would keep the nodes of pages that are gone; or its own row drafted).
  // Otherwise the form keeps its values (an uncoupled, undrafted form is
  // built on neither): only what the page keeps outside the form and built
  // on a row reloaded or discarded goes (onDiscarded(form)), when a row the
  // page changes with the form is reloaded (stashExtra: e.g. the endpoint of
  // the variant page, with its pending conditions) or the whole draft goes.
  // The form's row gone after the reload (a colleague deleted it; or a row
  // the draft created, discarded with it): the form closes (no row), and
  // the Item leaves for its list (the title's navigation, leaveRef), unless
  // the page shows something else (leaveWhenDeleted false: the variant
  // page, whose tabs follow the endpoint). After the render that closed
  // the form, while the Item is still mounted.
  const leaveRef = useRef(null);
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const formAfterReload = useCallback(
    (conflicts = []) => {
      const extraKeys = new Set(
        (stashExtra?.(formRef.current?.values) ?? []).map((op) =>
          draftKey(op.accessor, op.kind === "create" ? op.row?.id : op.id)
        )
      );
      const resetWithDraft = formToDiscard();
      // The row of the form: its id now (a rename not saved yet), or the
      // stored one
      const formRow = touchesRow(conflicts, accessor, [
        item.itemId,
        item.prevItemId
      ]);
      const extraRow = conflicts.some(({ type, accessor: table, id }) =>
        extraKeys.has(draftKey(type ?? table, id))
      );

      return (discarded = []) => {
        const wholeDraft =
          Boolean(draftScope) && discarded.includes(draftScope);

        if (formRow || (wholeDraft && resetWithDraft)) {
          formRef.current?.resetForm();
          onDiscarded?.();
        } else if (extraRow || wholeDraft) onDiscarded?.(formRef.current);

        const gone =
          !item.isNew &&
          Boolean(item.prevItemId) &&
          db.peek(accessor, item.prevItemId) === undefined;
        if (gone && leaveWhenDeleted)
          setTimeout(() => {
            if (mountedRef.current) leaveRef.current?.();
          }, 0);
      };
    },
    [
      accessor,
      draftScope,
      formToDiscard,
      item.isNew,
      item.itemId,
      item.prevItemId,
      leaveWhenDeleted,
      onDiscarded,
      stashExtra
    ]
  );

  // "Discard my changes and reload" of conflicts (the dialog, the banner's
  // Reload): the stored rows of the conflicts reloaded, each in its own
  // scope (the other changes of the drafts stay; the row of the form gets
  // the row its hold kept, and the drafts that depend on it go, resolve.js),
  // and the form's changes built on them (formAfterReload, asked first).
  // Resolves once reloaded; rejects when the stored rows can't be fetched.
  // The form's row gone: the Item leaves (formAfterReload).
  const reloadConflicts = useCallback(
    (conflicts) => {
      const afterReload = formAfterReload(conflicts);
      return resolveConflicts(conflicts).then(({ discarded = [] } = {}) =>
        afterReload(discarded)
      );
    },
    [formAfterReload, resolveConflicts]
  );

  // "Mine" of a conflict (the dialog's mineOf(accessor, id)): the form's
  // values for its row (its id now, or the stored one), the journal's row
  // for a row of a draft, unknown otherwise. Read when asked: the values
  // change at every keystroke.
  const mineOf = useCallback(
    (table, id) =>
      table === accessor && (id === item.prevItemId || id === item.itemId)
        ? formRef.current?.values
        : draftMineOf(table, id),
    [accessor, item.itemId, item.prevItemId]
  );
  const readValues = useCallback(() => formRef.current?.values, []);
  const getForm = useCallback(() => formRef.current, []);

  // "Apply my changes on their version" (the dialog and the banner:
  // onRebase(items), each item with its merged row). The rows of a draft
  // are rebased by their draft (draft.rebaseConflicts, which merges them
  // again on what it has now; the form's row drafted, with the form's
  // values as mine). The row of the form: their row goes into
  // MemoryDB (the hold applied, reloadHeld; or the draft's rebase when the
  // row is drafted), Formik reinitialises the form on it, then the Item
  // sets the merged values (the effect on item.item below: a setValues in
  // this tick would be undone by Formik's reinitialise, which runs first).
  // The form's row only while its conflict is the one the preview merged
  // on (a newer save of theirs meanwhile: refused, the dialog says so).
  // Resolves { rebased, refused }: a refused row leaves everything as it
  // was (the dialog stays open). Their version reaching the form this way
  // is no colleague's save for the safety net above (rebaseRef): the drafts
  // that depend on the form's row stay.
  const rebaseConflicts = useCallback(
    (items = []) => {
      const ids = [item.prevItemId, item.itemId];
      const isForm = ({ accessor: table, id }) =>
        !item.isNew && table === accessor && ids.includes(id);
      const own = items.find(isForm);
      const others = items.filter((one) => one !== own);
      const keyOf = ({ accessor: table, id }) => draftKey(table, id);
      const rebased = [];
      const refused = [];

      let ownHeld = false;
      if (own) {
        const held = draft.formConflictOf(accessor, item.prevItemId);
        ownHeld = Boolean(held?.own);
        const theirs = draft.conflictVersions(accessor, item.prevItemId)?.theirs;
        const drafted = draft.ownerOf(accessor, item.prevItemId);
        const sameTheirs =
          theirs && Number(theirs.version ?? 0) === Number(own.version ?? 0);
        if (!sameTheirs || (!held && !drafted))
          refused.push({ key: keyOf(own), reason: "changed" });
        else if (drafted) others.push(own);
      }

      // The rows of a draft, each in its own (the form's row when drafted)
      const byScope = new Map();
      for (const one of others) {
        const scope = draft.ownerOf(one.accessor, one.id);
        if (!scope) {
          refused.push({ key: keyOf(one), reason: "none" });
          continue;
        }
        if (!byScope.has(scope)) byScope.set(scope, []);
        byScope.get(scope).push(keyOf(one));
      }
      if (refused.length) return { rebased, refused };

      if (own) {
        rebaseRef.current = {
          version: Number(own.version ?? 0),
          values: own.row,
          // Our own save come back (the form edited on an older version)
          ...(ownHeld && {
            notice: "Your edits were applied on your saved version: save to keep them"
          })
        };
      }
      // The form's row drafted: mine is what the form has now (the row its
      // stash would draft, its id the drafted one), not the journal's row,
      // older than what is typed (a field changed by both in the journal,
      // put back in the form, merges as the preview showed)
      const typed = own && others.includes(own) && formRowRef.current();
      const formMine = typed
        ? { ...typed, id: item.prevItemId }
        : undefined;
      const mergeRows = formMine
        ? (args) =>
            args.accessor === accessor &&
            (args.base?.id ?? args.theirs?.id) === item.prevItemId
              ? merge({ ...args, mine: cloneDeep(formMine) })
              : merge(args)
        : merge;
      for (const [scope, keys] of byScope) {
        const result = draft.rebaseConflicts(scope, keys, mergeRows) ?? {};
        rebased.push(...(result.rebased ?? []));
        refused.push(...(result.refused ?? []));
      }
      // The form follows its row: rebased by its draft, or not at all when
      // a row of the drafts was refused (the held row stays held)
      const ownKey = own && keyOf(own);
      if (own && !rebased.includes(ownKey)) {
        if (refused.length || draft.ownerOf(accessor, item.prevItemId))
          rebaseRef.current = null;
        else {
          draft.reloadHeld(accessor, item.prevItemId);
          rebased.push(ownKey);
        }
      }
      // The form's row says so when its values are set (the effect below);
      // rows of the drafts alone, here
      if (!own && rebased.length && !refused.length)
        createNotification(
          "success",
          `Your changes were applied on their version of ${rebased.length} item${
            rebased.length === 1 ? "" : "s"
          }: save to keep them`
        );
      return { rebased, refused };
    },
    [accessor, createNotification, item.isNew, item.itemId, item.prevItemId]
  );

  // The claim of a create that went through with values typed since
  // (claimSaved below): the row claimed is held (a registration of its own,
  // dirty) from its fetch until the form is on it with those values. The
  // Item registers its row only once the item is the stored one, and its
  // form is clean until the values are set: a colleague's save meanwhile
  // would reach the form (reinitialised on their row, the values lost).
  // Released once the form rendered the values (claimPutBack: the same
  // render as Formik's), or when the Item goes.
  const claimHoldRef = useRef(null);
  const [claimPutBack, setClaimPutBack] = useState(0);
  const releaseClaimHold = useCallback(() => {
    claimHoldRef.current?.();
    claimHoldRef.current = null;
  }, []);
  useEffect(() => {
    if (claimPutBack) releaseClaimHold();
  }, [claimPutBack, releaseClaimHold]);
  useEffect(() => releaseClaimHold, [releaseClaimHold]);

  // Their row is in the form (reinitialised on it): the merged values go on
  // top, the form is modified (Save sends them, based on their version).
  // Also a create retried after it went through, with values typed since
  // (claimSaved below, its own notice)
  useEffect(() => {
    const rebase = rebaseRef.current;
    if (!rebase || !item.item) return;
    if (Number(item.item.version ?? 0) !== rebase.version) return;
    rebaseRef.current = null;
    const formik = formRef.current;
    if (!formik) {
      releaseClaimHold();
      return;
    }
    formik.setValues(item.fromRow(cloneDeep(rebase.values)));
    if (claimHoldRef.current) setClaimPutBack((n) => n + 1);
    onRebased?.(formik);
    createNotification(
      "success",
      rebase.notice ??
        "Your changes were applied on their version: save to keep them"
    );
    // Only when the row of the form changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.item]);

  // The row of a save of ours not answered goes under the form while it has
  // changes (draft: OUR OWN SAVES, match "expected": the response of its
  // Save was lost, the save went through, and the user typed after it; a
  // reload or a fetch brings the row): told before the row is written. The
  // form's values are what that Save sent and what was typed after it: they
  // stay whole, set on our row as a rebase sets them (rebaseRef, the effect
  // above), with a notice: the form is modified, its next Save is based on
  // our version. Nothing typed after it (the form is the row): the form
  // follows the row. Only for that match: the saved row of an answered save
  // under a form based on another row since is held as any row (its banner,
  // Apply mine on theirs), never merged here.
  // The notice says both: that save had gone through (the user was told
  // "Not saved"), and what was typed after it is kept. ownKeptRef: the
  // version our row was kept at, so that the refused Save clicked again
  // (whose 409 brought the row) says nothing more (showSaveConflicts).
  const ownKeptRef = useRef(null);
  ownRowUnderRef.current = (row, { match } = {}) => {
    const formik = formRef.current;
    if (match !== "expected" || !formik || !formDirtyRef.current()) return;
    const values = cloneDeep(formik.values ?? emptyObj);
    const sent = onSubmit ? onSubmit(cloneDeep(values)) : values;
    if (!changedSince(accessor, row, sent)) return;
    ownKeptRef.current = Number(row.version ?? 0);
    rebaseRef.current = {
      version: Number(row.version ?? 0),
      // The row's own fields (what the server keeps)
      values: {
        ...values,
        id: row.id,
        version: row.version,
        lastSaved: row.lastSaved,
        refs: row.refs,
        refsComputed: row.refsComputed
      },
      notice: `${EARLIER_SAVED}; your later changes are kept, save again`
    };
  };

  // A refused save: the form keeps its values (Save is refused again until
  // the user reloads or rebases); the dialog shows what changed, and offers
  // "Discard my changes and reload" (reloadConflicts), "Apply my changes on
  // their version" (rebaseConflicts), "Copy my values" and "Keep editing".
  // A 409 has no data: presentConflicts fetches the stored rows first (a
  // row of the form comes back held while the form is dirty).
  // A conflict whose row, fetched, is a save of ours at the version the
  // server refused with (Save clicked again after "Not saved": the first
  // went through, its response lost; draft: OUR OWN SAVES) is no conflict:
  // the row is the form's base now; the dialog shows the others, if any
  // (presentConflicts: onOwnSaved). One notice: "Saved: your earlier Save
  // had gone through", unless the form kept what was typed after it on that
  // row (ownKeptRef), whose notice says it already.
  const showSaveConflicts = useCallback(
    (conflicts) =>
      presentConflicts({
        conflicts,
        onDiscard: () => reloadConflicts(conflicts),
        onRebase: rebaseConflicts,
        mineOf,
        onOwnSaved: (own) => {
          const kept = own.some(
            ({ type, accessor: table, id, storedVersion }) =>
              (type ?? table) === accessor &&
              (id === item.prevItemId || id === item.itemId) &&
              Number(storedVersion) === ownKeptRef.current
          );
          if (!kept) createNotification("success", EARLIER_SAVED);
        }
      }),
    [
      accessor,
      createNotification,
      item.itemId,
      item.prevItemId,
      mineOf,
      rebaseConflicts,
      reloadConflicts
    ]
  );

  // The banner of the form's row saved (or deleted) by someone else while
  // the form has changes (held, see the registration): their version is
  // known, so "What changed" and Rebase need no fetch.
  const formConflict = useFormConflict(
    accessor,
    item.isNew ? null : item.prevItemId
  );
  const formConflictVersions = useMemo(
    () =>
      formConflict ? draft.conflictVersions(accessor, formConflict.id) : null,
    [accessor, formConflict]
  );
  // Reload of the banner: the form's changes are lost (asked first; the
  // drafts that depend on its row go too, and the question says so). A row
  // they deleted: the form closes, and the Item leaves (reloadConflicts).
  const reloadForm = useCallback(() => {
    const conflict = draft.formConflictOf(accessor, item.prevItemId);
    if (!conflict) return;
    const conflicts = [{ type: accessor, ...conflict }];
    const whole = wholeDraftsOf(conflicts);
    const who = conflict.lastSaved?.username ?? "someone";
    const deleted = conflict.action === "delete";

    requestConfirm(
      `${
        deleted
          ? `Discard your changes? ${who} deleted this ${props.name}.`
          : conflict.own
            ? "Discard the edits made on the older version and load your saved version?"
            : `Discard your changes and load the version ${who} saved?`
      }${whole.length ? ` ${dependentsSentence(whole, "Reloading it")}` : ""}`,
      () =>
        reloadConflicts(conflicts).catch((error) => {
          console.error("⚠️ RELOAD OF THE FORM", error);
          createNotification("error", String(error?.message ?? error));
        })
    );
  }, [
    accessor,
    createNotification,
    item.prevItemId,
    props.name,
    reloadConflicts,
    requestConfirm
  ]);

  // Show changes of the banner: the dialog of a refused Save, for the
  // conflict held now. Its Discard is the choice (no second question): the
  // reload, which keeps the dialog open with its error when it fails.
  const showFormConflict = useCallback(() => {
    const conflict = draft.formConflictOf(accessor, item.prevItemId);
    if (!conflict) return;
    return showSaveConflicts([{ type: accessor, ...conflict }]);
  }, [accessor, item.prevItemId, showSaveConflicts]);

  const handleInit = useCallback(() => {
    let values = item.item ?? emptyValuesObj;

    if (!initiated) {
      if (onInit) {
        const newValues = onInit(cloneDeep(values), item);
        if (newValues) {
          values = newValues;
          setCurrentValues(newValues);
          item.setItemValues(newValues);
        }
      }
      setInitiated(true);
    }

    return values;
  }, [emptyValuesObj, initiated, item, onInit]);

  const logActive = useCallback(
    (action) => {
      const body = { action, ...activeBodyRef.current };

      isActive(body, (data) => {
        if (data) {
          setLocked(data.currentActive ? data.currentActive.username : false);
        }
      });
    },
    [isActive]
  );

  useEffect(() => {
    if (!activeBodyRef.current && !item.isNew && item.item) {
      activeBodyRef.current = {
        id: item.itemId,
        title: item.item.title,
        type: props.name,
        accessor,
        tenant
      };
      logActive("enter");
    }
  }, [
    accessor,
    item.isNew,
    item.item,
    item.itemId,
    logActive,
    props.name,
    tenant
  ]);

  useEffect(() => {
    return () => {
      if (activeBodyRef.current) {
        logActive("exit");
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The page of the item id, next to this one (a new item saved:
  // /blocks/newItem -> /blocks/<id>)
  const pathOf = useCallback(
    (id) => {
      const pathname = location.pathname;
      return `${pathname.substring(0, pathname.lastIndexOf("/"))}/${id}`;
    },
    [location.pathname]
  );

  // A create refused because the item exists, saved last by this user: a
  // Save of the new item that went through while its answer was lost (a
  // network error, a timeout), clicked again. The row is ours: no
  // conflict.
  const savedBefore = useCallback(
    (conflicts = []) => {
      if (!item.isNew || item.prevItemId !== NEW_ITEM) return false;
      if (conflicts.length !== 1) return false;
      const [conflict] = conflicts;
      return (
        conflict.reason === "exists" &&
        (conflict.type ?? conflict.accessor) === accessor &&
        conflict.id === item.itemId &&
        savedByUser(conflict.lastSaved, {
          nickname: userNickname,
          name: userName
        })
      );
    },
    [accessor, item.isNew, item.itemId, item.prevItemId, userName, userNickname]
  );

  // The item is the stored one from now on (as after its Save): its row is
  // fetched, the form opens on it, and the page is the item's. Not found
  // (the fetch failed and no socket brought it): the conflict dialog, as
  // for any id taken, rather than a form with no row.
  // The stored row is what that Save sent: what was typed after it (the
  // form's values of now, as the Save would send them, differ from it) is
  // not stored. It is kept: the form is set on the stored row with those
  // values (rebaseRef, as a rebase: the effect above, once the item is the
  // stored row), modified, and its next Save is based on the stored
  // version. Nothing typed since: the form is the stored item, saved.
  // A row known as ours by the display name alone (no lastSaved.nickname:
  // saved before the server wrote it) may be a namesake colleague's: it is
  // ours only when it is what the Save sends (nothing put back on it);
  // otherwise the id is taken (the dialog: choose another id), and the
  // form stays new with its values.
  // The row fetched at another version than the 409's (saved again since,
  // by someone else): not the row our Save stored, the dialog too. Fetched
  // and claimed, the row is held until the form is on it with the values
  // (claimHoldRef): a colleague's save meanwhile is their conflict (the
  // banner), MemoryDB and the form stay on the row claimed.
  const claimSaved = useCallback(
    (conflicts) => {
      const id = item.itemId;
      const byName = conflicts[0]?.lastSaved?.nickname == null;
      const claimedVersion = conflicts[0]?.storedVersion;
      Promise.resolve(reloadItems([{ accessor, id }]))
        .catch((error) => console.warn("⚠️ RELOAD OF THE SAVED ITEM", error))
        .then(() => {
          const stored = db.peek(accessor, id);
          const savedSince =
            stored !== undefined &&
            claimedVersion != null &&
            Number(stored.version ?? 0) !== Number(claimedVersion);
          if (stored === undefined || savedSince) {
            showSaveConflicts(conflicts);
            return;
          }
          const values = cloneDeep(formRef.current?.values ?? emptyObj);
          const sent = onSubmit ? onSubmit(cloneDeep(values)) : values;
          const later = changedSince(accessor, stored, sent);
          if (later && byName) {
            showSaveConflicts(conflicts);
            return;
          }
          if (later) {
            rebaseRef.current = {
              version: Number(stored.version ?? 0),
              // The stored row's own fields (what the server keeps)
              values: {
                ...values,
                id,
                version: stored.version,
                lastSaved: stored.lastSaved,
                refs: stored.refs,
                refsComputed: stored.refsComputed
              },
              notice: "Saved earlier: your later changes are kept, save again"
            };
            // Held from now (MemoryDB on the row read above), dirty
            releaseClaimHold();
            claimHoldRef.current = draft.registerOpenForm(accessor, id);
          }
          item.claimSaved(id);
          if (onSubmitted) onSubmitted(id, true);
          navigate(pathOf(id));
          if (later) return;
          setDataStatus("diff", emptyObj);
          createNotification(
            "success",
            "Saved: the item already existed with your last save"
          );
        });
    },
    [
      accessor,
      createNotification,
      item,
      navigate,
      onSubmit,
      onSubmitted,
      pathOf,
      releaseClaimHold,
      reloadItems,
      setDataStatus,
      showSaveConflicts
    ]
  );

  // Something threw before the request left (onSubmit, saveExtra, the merge
  // of the draft, inheritFromBatch, the checks of the write): nothing was
  // sent. The Save no longer runs, nor the save of its scope (its collect
  // may have run: Discard, Reload, Rebase and Reset wait for it), and the
  // user is told; the form keeps its values.
  const saveFailed = useCallback(
    (error) => {
      console.error("⚠️ SAVE OF THE FORM", error);
      if (draftScope) draft.saveSettled(draftScope);
      setIsSaving(false);
      createNotification(
        "error",
        `Not saved: ${String(error?.message ?? error)}`
      );
    },
    [createNotification, draftScope, setIsSaving]
  );

  const submitValues = useCallback(
    (values, { setSubmitting }) => {
      setIsSaving(true);
      setSubmitting(false);

      // Only the draft changed: it is saved alone (the stored item is not
      // saved again unchanged, with a new version)
      if (draftScope && !item.isNew && draft.isDirty(draftScope)) {
        const formDirty = isDirty
          ? isDirty(values, item.item ?? emptyValuesObj)
          : formRef.current?.dirty;

        if (!formDirty) {
          // The row of the form drafted in the scope (stashed, then opened
          // again) is sent: its new version is this Save's (see the
          // colleague's save), unless it arrived already. Not sent, a new
          // version is someone else's.
          const versionAtSave = rowVersionRef.current;
          const sendsFormRow =
            Boolean(item.prevItemId) &&
            draft.ownerOf(accessor, item.prevItemId) === draftScope;
          return saveDraft().then((status) => {
            if (status === "OK") {
              ownSaveRef.current =
                sendsFormRow && rowVersionRef.current === versionAtSave;
              setDataStatus("diff", emptyObj);
              if (onSubmitted) onSubmitted(item.itemId, false);
            }
            setIsSaving(false);
          }, saveFailed);
        }
      }

      // Refused before it leaves, with the conflict dialog: the rows the
      // page saves with the form that someone else saved since the page
      // changed them (extraConflicts); the conflict of the form's row when
      // its id is renamed (the write sends the new id, so the local refusal
      // of useDataRemote, by the ids sent, does not see the conflict of the
      // old one). Otherwise the conflicts of the rows sent (held or live)
      // are refused by useDataRemote (onError below).
      const refusedHere = [
        ...(!item.isNew && item.itemId !== item.prevItemId
          ? draft.conflictsOf([draftKey(accessor, item.prevItemId)])
          : []),
        ...(extraConflicts?.() ?? [])
      ];
      if (refusedHere.length) {
        setIsSaving(false);
        showSaveConflicts(refusedHere);
        return;
      }

      const data = onSubmit ? onSubmit(cloneDeep(values)) : values;
      // The version of the row this Save sends (see the colleague's save)
      const versionAtSave = rowVersionRef.current;
      // What the page saves together with the item, in the same request,
      // with the draft of its scope
      const pageExtra = saveExtra ? saveExtra(data) : undefined;
      // The row of the form created by the draft (a new variant): sent as
      // new (isNew, baseVersion 0), as the draft would send it
      const created =
        Boolean(draftScope) &&
        !item.isNew &&
        draft.storedOriginal(accessor, item.prevItemId) === null;
      const extra = draftScope
        ? mergeDraft(pageExtra, draft.collect(draftScope), {
            accessor,
            Id: item.itemId
          })
        : pageExtra;

      return new Promise((resolve) => {
        // inheritFromBatch and the checks of the write run before the
        // request leaves (useItem, useDataRemote): a throw there is a Save
        // that failed (saveFailed), not one left running
        const send = () =>
          item.onSubmission(
            data,
            (id, isNew) => {
              // The new version of the row is ours, when it arrives
              // (unless it arrived already)
              ownSaveRef.current = rowVersionRef.current === versionAtSave;
              if (draftScope) draft.clear(draftScope);
              if (onSubmitted) onSubmitted(id, isNew);

              if (isNew) navigate(pathOf(id));

              setDataStatus("diff", emptyObj);
              setIsSaving(false);
              resolve(data);
            },
            {
              extra,
              asNew: created,
              // Not saved (the error is notified, a conflict opens the
              // dialog): the form keeps its values, Save can be clicked
              // again
              onError: (status, error) => {
                setIsSaving(false);
                if (error?.type === "conflict") {
                  const conflicts = error.conflicts ?? [];
                  if (savedBefore(conflicts)) claimSaved(conflicts);
                  else showSaveConflicts(conflicts);
                }
                resolve();
              }
            }
          );
        try {
          send();
        } catch (error) {
          saveFailed(error);
          resolve();
        }
      });
    },
    [
      accessor,
      claimSaved,
      draftScope,
      emptyValuesObj,
      extraConflicts,
      isDirty,
      item,
      navigate,
      onSubmit,
      onSubmitted,
      pathOf,
      saveDraft,
      saveExtra,
      saveFailed,
      savedBefore,
      setDataStatus,
      setIsSaving,
      showSaveConflicts
    ]
  );

  // Formik's onSubmit: a throw of the Save before its request left is a
  // Save that failed, not one left running
  const handleSubmission = useCallback(
    (values, formikBag) => {
      try {
        return submitValues(values, formikBag);
      } catch (error) {
        saveFailed(error);
      }
    },
    [saveFailed, submitValues]
  );

  // The errors of values: an object, or a promise of it when onValidate is
  // asynchronous
  const validateValues = useCallback(
    (values) => {
      const errors = {};

      if (!values.title) errors.title = "You Must insert a Title";
      const validation = onValidate
        ? onValidate.call({ ...item, formik: formRef.current, tenant }, values)
        : emptyObj;

      if (validation.then) {
        return validation.then((validationErrors) => ({
          ...validationErrors,
          ...errors
        }));
      } else return { ...validation, ...errors };
    },
    [item, onValidate, tenant]
  );

  const handleValidation = useCallback(
    (values) => {
      setCurrentValues(values);
      return validateValues(values);
    },
    [validateValues]
  );

  // Back to the list, through the guards of a navigation (useConfirmNavigation:
  // the form's changes, the drafts it leaves)
  const title = useMemo(
    () => (
      <ItemTitle
        destination={`/${props.linkBack ?? accessor}`}
        name={props.name}
        leaveRef={leaveRef}
      />
    ),
    [accessor, props.linkBack, props.name]
  );

  // The scheduling of the form, for the Scheduled popover of the header: it
  // shows the values of the form and a drag changes them (saved by Save). A
  // new object only when one of the fields it reads changes.
  const {
    id: valuesId,
    title: valuesTitle,
    scheduled,
    rule,
    blocks
  } = currentValues ?? emptyValuesObj;
  const scheduleValues = useMemo(
    () => ({ id: valuesId, title: valuesTitle, scheduled, rule, blocks }),
    [blocks, rule, scheduled, valuesId, valuesTitle]
  );

  const copyId = useCallback(() => {
    setIsCopied(true);
    copy(item.itemId);
    setTimeout(() => setIsCopied(false), 2000);
  }, [copy, item.itemId]);

  const header = useMemo(() => {
    return (
      <div className="header">
        <span className="id-label">#Id:</span>{" "}
        <span className="id">{item.itemId}</span>{" "}
        {isCopied ? (
          <div className={styles.copied}>ID Copied</div>
        ) : (
          <FontAwesomeIcon
            icon={faClipboard}
            className="copyId"
            onClick={copyId}
          />
        )}
        {scheduleValues.scheduled ? (
          <OverlayTrigger
            trigger="click"
            key={`scheduling-${item.accessor}`}
            placement="bottom"
            overlay={
              <Popover
                id={`popover-positioned-bottom`}
                bsPrefix="ares-scheduling-item-pop"
                placement="bottom"
              >
                <Popover.Body>
                  <FormScheduleDiagram
                    itemType={item.accessor}
                    values={scheduleValues}
                    formRef={formRef}
                  />
                </Popover.Body>
              </Popover>
            }
          >
            <span className="schedule-block">
              <FontAwesomeIcon icon={faCalendar} />
              <span className="schedule-item-label">Scheduled</span>
            </span>
          </OverlayTrigger>
        ) : null}
      </div>
    );
  }, [copyId, isCopied, item.accessor, item.itemId, scheduleValues]);

  const copyItem = useCallback(
    () =>
      copy(JSON.stringify({ _ares: item.accessor, item: item.item, tenant })),
    [copy, item.accessor, item.item, tenant]
  );

  // Paste of the Item's Copy: another item, its fields as a copy (the title
  // " [Copy]", no refs, version or lastSaved). Of "Copy my values" (clip.mine:
  // the user's own values of a conflict, e.g. copied before a Reload) of
  // this row (its id now, or the stored one; a new item's values have no id,
  // put back in a new item): the values as they were typed, on the row the
  // form has now (its id, version, lastSaved and refs), the title as it
  // was. My values of another row are a copy of it.
  const pasteItem = useCallback(
    (data, clip) => {
      const sameRow =
        data?.id == null
          ? item.isNew
          : data.id === item.itemId || data.id === item.prevItemId;
      if (clip?.mine && sameRow) {
        const now = formRef.current.values ?? emptyObj;
        data.id = item.itemId;
        data.version = now.version;
        data.lastSaved = now.lastSaved;
        data.refs = now.refs;
        data.refsComputed = now.refsComputed;
      } else {
        data.id = item.itemId;
        data.lastSaved = undefined;
        data.version = undefined;
        data.refs = undefined;
        data.refsComputed = undefined;
        data.title = `${data.title} [Copy]`;
      }

      nameRef?.current?.setFold?.(true);
      formRef.current.setValues(data);
    },
    [item.isNew, item.itemId, item.prevItemId]
  );

  // The header of the form but its refs: what it shows changes with the
  // conflicts, the lock and the row, never with a keystroke. Its own
  // element (memoised apart from the refs, which follow the values): at a
  // keystroke React finds the same element and skips it, the banners
  // included (the endpoint's of the variant page reads its stored row).
  const extraRest = useMemo(() => {
    const label = capitalize(item.name);

    return (
      <>
        {locked ? (
          <div className={styles.locked}>
            <FontAwesomeIcon
              icon={faExclamationCircle}
              className={styles.lockIcon}
            />
            <b>{locked}</b> is editing this {props.name}
          </div>
        ) : null}
        <ConflictBanner
          conflict={formConflict}
          versions={formConflictVersions}
          mine={readValues}
          onReload={reloadForm}
          onRebase={rebaseConflicts}
          onShowChanges={showFormConflict}
          className={styles.conflictBanner}
        />
        {typeof banner === "function" ? banner(getForm) : (banner ?? null)}
        <div className={styles.toolbar}>
          <ClipboardMenu
            label={label}
            accessor={item.accessor}
            tenant={tenant}
            onCopy={copyItem}
            onPaste={pasteItem}
          />
        </div>
      </>
    );
  }, [
    banner,
    copyItem,
    formConflict,
    formConflictVersions,
    getForm,
    item.accessor,
    item.name,
    locked,
    pasteItem,
    props.name,
    readValues,
    rebaseConflicts,
    reloadForm,
    showFormConflict,
    tenant
  ]);

  const extra = useMemo(
    () => (
      <div className={styles.extraBox}>
        <RefBox
          accessor={item.accessor}
          data={currentValues}
          formikRef={formRef}
        />

        {extraRest}
      </div>
    ),
    [currentValues, extraRest, item.accessor]
  );

  if (!item) return null;

  const errors = formRef.current?.errors ?? emptyObj;
  const isInvalid = Object.keys(errors).length > 0;

  return (
    <PendingEditsContext.Provider value={pendingEdits}>
      <Container
        title={title}
        help={`item-${props.accessor}`}
        color="#111"
        coloredBars={true}
        coloredHeader={false}
        foldable={false}
        header={header}
        extra={extra}
        footer={
          <>
            {draftScope ? (
              <SaveBar
                scope={draftScope}
                embedded
                formToDiscard={formToDiscard}
                formAfterReload={formAfterReload}
                // Apply mine on theirs, Show changes: the form's values for
                // its row (drafted: the journal's row is older than what is
                // typed), and the form set on the merged row after the
                // rebase writes it, as the dialog of its Save does
                onRebase={rebaseConflicts}
                mineOf={mineOf}
              />
            ) : null}
            <Button
              type="reset"
              variant="outline-secondary"
              size="sm"
              onClick={resetFormAndDraft}
              // Not while a Save runs (its own, or one of its draft):
              // the response would bring back what Reset threw away
              disabled={!formRef.current?.dirty || isSaving || scopeSaving}
            >
              Reset
            </Button>
            &nbsp;
            <SubmitButton
              branch={branch}
              canSave={canSave}
              isInvalid={isInvalid}
              isSaving={isSaving}
              locked={locked}
              canWrite={item.permissions.write.includes(props.accessor)}
              formRef={formRef}
              draftScope={draftScope}
              styles={styles}
            />
            <SaveButton
              errors={errors}
              isInvalid={isInvalid}
              formik={formRef.current}
            />
          </>
        }
        headerOnlyOnFolded={false}
        hover={false}
      >
        <Fragment>
          {item.isNew !== true && item.item === undefined ? null : (
            <Formik
              initialValues={handleInit()}
              enableReinitialize={true}
              validateOnMount={true}
              validate={handleValidation}
              onSubmit={handleSubmission}
              innerRef={formRef}
            >
              {(formikProps) => {
                const dirty = props.dirty
                  ? props.dirty(formikProps.values, item.item ?? emptyValuesObj)
                  : formikProps.dirty;

                const stopSave =
                  !item.permissions.write.includes(props.accessor) ||
                  !formikProps.isValid ||
                  formikProps.isSubmitting ||
                  !dirty;

                //dirty && console.log(require("variable-diff")(item.item, formikProps.values).text);

                return (
                  <Fragment>
                    <FormSettled dirty={dirty} onSettled={formSettled} />
                    <FormStatus
                      dirty={dirty}
                      coupledScope={coupledScope({
                        dirty,
                        draftScope,
                        couple: coupleDraft,
                        values: formikProps.values,
                        initialValues: formikProps.initialValues
                      })}
                      canSave={!stopSave}
                      original={item.item}
                      values={formikProps.values}
                    />
                    <Form
                      onSubmit={(e) => {
                        // Implicit submit (Enter): same guard as the Save button
                        if (pendingEdits.isPending()) {
                          e.preventDefault();
                          return;
                        }
                        formikProps.handleSubmit(e);
                      }}
                    >
                      <ItemContext.Provider
                        value={{ ...item, openFormRelease }}
                      >
                        {showTitleAndDesc ? (
                          <Fragment>
                            <NameAndDescription
                              isNew={item.isNew}
                              ref={nameRef}
                              color={props.NameAndDescriptionColor}
                              titleLocked={props.titleLocked?.(item)}
                            />
                          </Fragment>
                        ) : null}

                        {props.children}
                      </ItemContext.Provider>
                    </Form>
                  </Fragment>
                );
              }}
            </Formik>
          )}
        </Fragment>
      </Container>
    </PendingEditsContext.Provider>
  );
}

// Save is disabled while a conditions box has an edit not handed over yet.
// It reads the pending state here, so that only this button re-renders. With
// a draftScope it saves the draft also when the form is unchanged.
function SubmitButton({
  branch,
  canSave: formCanSave,
  isInvalid,
  isSaving,
  locked,
  canWrite,
  formRef,
  draftScope,
  styles
}) {
  const pendingEdits = useHasPendingEdits();
  const draftCount = useDraftCount(draftScope);
  const canSave =
    formCanSave || (draftCount > 0 && canWrite && !isInvalid && !isSaving);

  return (
    <Button
      type="submit"
      size="sm"
      disabled={branch === "main" || pendingEdits}
      variant={
        isInvalid
          ? "outline-danger"
          : canSave && !pendingEdits
            ? "success"
            : "outline-success"
      }
      onClick={() => {
        if (canSave && !isInvalid && !pendingEdits && formRef.current) {
          formRef.current.handleSubmit();
        }
      }}
    >
      {isSaving ? (
        <FontAwesomeIcon icon={faSpinner} pulse />
      ) : canWrite && branch !== "main" ? (
        <>
          {canSave && locked && (
            <FontAwesomeIcon
              icon={faExclamationCircle}
              className={styles.lockIconSave}
            />
          )}
          Save
        </>
      ) : (
        <FontAwesomeIcon icon={faLock} />
      )}
    </Button>
  );
}

// The title of the Item: a click goes back to the list (destination) as any
// guarded navigation does (useConfirmNavigation: the form's changes are
// stashed or asked about, the drafts left are asked about). Its own
// component: only it re-renders when the unsaved status changes.
// leaveRef.current(): the same navigation, for the Item (a row deleted).
function ItemTitle({ destination, name, leaveRef }) {
  const confirmNavigation = useConfirmNavigation();
  const leave = useCallback(
    () => confirmNavigation(destination),
    [confirmNavigation, destination]
  );
  useEffect(() => {
    leaveRef.current = leave;
  }, [leave, leaveRef]);

  return (
    <div className="title-box" onClick={leave}>
      <span className="go-back">
        <FontAwesomeIcon icon={faAngleDoubleLeft} />
      </span>
      <span className="title">{name}</span>
    </div>
  );
}

// onSettled() when the form turns clean: not modified (dirty) and no edit of
// a conditions box pending. It reads the pending state here, so that only
// this component re-renders when it changes; the effect runs on a flip only.
function FormSettled({ dirty, onSettled }) {
  const pending = useHasPendingEdits();
  const settled = !dirty && !pending;
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  useEffect(() => {
    if (settled) onSettledRef.current();
  }, [settled]);

  return null;
}

// The diff is shown only by the differences tooltip of the Save button.
const DIFF_DELAY = 250;

// The FormStatus that published the status last: only it clears it when it
// unmounts (an Item mounting in the same commit, e.g. the next variant tab,
// publishes its own).
let statusOwner = null;

// Publishes the form status to the data state after each render of the form,
// as the render prop did before. The diff of the whole item, which is
// expensive, is recomputed only when the item or the values change, and once
// typing pauses (the first time right away).
// Unmounted (the form closed, or its row removed: the Discard of a created
// variant), it clears unsavedData and unsavedScope: a stale "unsaved" would
// make the next navigation ask about a form that is not there.
function FormStatus({ dirty, coupledScope, canSave, original, values }) {
  const setDataStatus = useDataSetStatus();
  const diffedRef = useRef(false);
  const ownerRef = useRef(null);
  if (!ownerRef.current) ownerRef.current = {};
  const setDataStatusRef = useRef(setDataStatus);
  setDataStatusRef.current = setDataStatus;

  useEffect(() => {
    statusOwner = ownerRef.current;
    setDataStatus("unsavedData", dirty);
    // Leaving the form discards the draft it is coupled with
    // (useConfirmNavigation): the two halves of an operation go together
    setDataStatus("unsavedScope", coupledScope ?? null);
    setDataStatus("canSave", canSave);
  });

  useEffect(() => {
    const owner = ownerRef.current;
    return () => {
      if (statusOwner !== owner) return;
      statusOwner = null;
      setDataStatusRef.current("unsavedData", false);
      setDataStatusRef.current("unsavedScope", null);
    };
  }, []);

  useEffect(() => {
    const publishDiff = () => setDataStatus("diff", diff(original, values));

    if (!diffedRef.current) {
      diffedRef.current = true;
      publishDiff();
      return;
    }

    const timer = setTimeout(publishDiff, DIFF_DELAY);
    return () => clearTimeout(timer);
  }, [original, values, setDataStatus]);

  return null;
}

function SaveButton(props) {
  const { errors, isInvalid } = props;
  const { changed, text } = useDataStateSelector(
    (state) => state.diff ?? emptyObj
  );

  if (!changed && !isInvalid) return null;

  return (
    <div className={styles.differenceBox}>
      <OverlayTrigger
        key="save-differences"
        placement="top"
        trigger={["click"]}
        overlay={
          <Tooltip id="tooltip-save-differences" className={styles.Tooltip}>
            <div className={styles.differenceEditor}>
              <Editor
                width="700px"
                height="30vh"
                defaultLanguage="ruby"
                value={isInvalid ? JSON.stringify(errors, null, 2) : text}
                defaultValue="// Insert your code"
                theme="vs-dark"
                options={{ minimap: { enabled: false }, folding: true }}
              />
            </div>
          </Tooltip>
        }
      >
        <span className={styles.diffIcon}>
          <FontAwesomeIcon icon={faGripVertical} />
        </span>
      </OverlayTrigger>
    </div>
  );
}
